/*
  assets/cart-drawer.js: Craft's cart drawer, as the Magnolia slide-out cart tray (Seth, 2026-09-26).
  Markup: snippets/cart-drawer.liquid. Styles: assets/component-cart-drawer.css. Loaded only when
  Theme settings > Cart > Cart type is Drawer (layout/theme.liquid).

  What it adds to Craft's drawer, which it keeps (product-form.js still calls renderContents,
  getSectionsToRender and setActiveElement after "Buy door only", so the tray opens on the door just added):
  - the toggle (#MadTrayToggle): a tab on the right edge from 750 px up, the bottom strip on phones. A real
    button with aria-expanded and aria-controls; a click (or, on a phone, a swipe up) pulls the tray out, a
    second click pushes it away. It says what is in the cart and how many doors are on the estimate
    (window.MadEstimateList), and hides when both are empty;
  - the open tray is a dialog: focus moves into it and is kept there (Tab and Shift+Tab wrap), Escape, the
    overlay, the close button, the tab, and on phones a tap on the sheet's top edge or a swipe down close it,
    and focus returns to what opened it (the tab, the header's cart icon, or the Buy door only button);
  - on phones the strip steps aside while the door page's own sticky purchase bar is showing
    ([data-mad-sticky-cta]), while another dialog or menu is open and while the keyboard is up, so there is
    only ever one bar at the bottom;
  - the page does not jump when the tray locks scrolling (the scrollbar's width is kept as padding).
  Motion: 240 ms slides from the stylesheet; none with prefers-reduced-motion (nothing here waits for a
  transition to end).
*/

(function () {
  'use strict';
  if (window.MadCartTray) return;

  /* $1,895 or $1,895.50: whole dollars without cents, as the tray prints them (money_without_trailing_zeros). */
  function money(cents) {
    var n = Math.round(Number(cents) || 0);
    var whole = n % 100 === 0;
    var v = (n / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
    return '$' + v;
  }

  /* Doors on the browser estimate list (plan 5.4); 0 when the list is off or not loaded. */
  function estimateCount(list) {
    var L = list === undefined ? window.MadEstimateList : list;
    if (!L || !L.enabled) return 0;
    try { return Number(L.count()) || 0; } catch (e) { return 0; }
  }

  /* What the toggle and the tray's estimate line say. s: { count, total (cents), totalText, estimate }. */
  function summary(s) {
    var count = Math.max(0, Number(s.count) || 0), est = Math.max(0, Number(s.estimate) || 0);
    return {
      visible: count > 0 || est > 0,
      cart: count > 0,
      count: String(count),
      countWord: count === 1 ? ' item' : ' items',
      total: s.totalText || money(s.total),
      estimate: est > 0,
      estimateCount: String(est),
      estimateWord: est === 1 ? ' door' : ' doors',
      estimateLine: est + (est === 1 ? ' door' : ' doors')
    };
  }

  /* Is the door page's sticky purchase bar showing? It is not when it has the hidden attribute or data-mad-away
     (it shows only once the buy buttons have scrolled away), or when the stylesheet hides it (display none from
     750 px up and while a dialog is open, visibility hidden while away). Its position is not read: it slides,
     and the strip steps aside as soon as the bar starts to come in. */
  function stickyShowing(el, getStyle) {
    if (!el || el.hidden || (el.hasAttribute && el.hasAttribute('data-mad-away'))) return false;
    var cs = getStyle ? getStyle(el) : window.getComputedStyle(el);
    if (!cs || cs.display === 'none' || cs.visibility === 'hidden') return false;
    return el.getBoundingClientRect().height > 0;
  }

  /* A swipe down on the sheet: close when it went far enough (a quarter of the sheet, at most 120 px), or fast. */
  function shouldClose(dy, ms, sheetHeight) {
    if (!(dy > 0)) return false;
    var far = dy > Math.min(120, (Number(sheetHeight) || 480) * 0.25);
    var fast = ms > 0 && dy > 30 && dy / ms > 0.6;
    return far || fast;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  function focusables(root) {
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      if (el.closest('[hidden]') || el.closest('[aria-hidden="true"]')) return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  window.MadCartTray = { money: money, estimateCount: estimateCount, summary: summary, stickyShowing: stickyShowing, shouldClose: shouldClose, focusables: focusables };
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    /* The overlay, the close button and the sheet's top edge are re-rendered with the tray: one listener here. */
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('[data-mad-tray-close], [data-mad-tray-grab]')) {
        evt.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this.trayReady) return;
    this.trayReady = true;
    this.initTray();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    /* On the full cart page the icon stays a plain link: the cart is already on screen. */
    if (document.getElementById('main-cart-items')) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer-Dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      /* A new tab or window (Ctrl, Cmd, Shift, middle click) still opens the full cart at /cart. */
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* ── the tray ────────────────────────────────────────────────── */
  initTray() {
    document.documentElement.classList.add('mad-tray-on');
    this.toggle = document.getElementById('MadTrayToggle');

    if (this.toggle) {
      /* Rendered at the top of the page with the tray; a floating control belongs after the page in the tab order
         (the header's cart icon is the early way in). */
      if (this.toggle.nextElementSibling) document.body.appendChild(this.toggle);
      this.toggle.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.swiped) return;
        if (this.isOpen()) this.close();
        else this.open(this.toggle);
      });
      /* Phone: a swipe up on the strip pulls the sheet up. */
      this.toggle.addEventListener('touchstart', (e) => { this.stripY = e.touches[0].clientY; }, { passive: true });
      this.toggle.addEventListener('touchmove', (e) => {
        if (this.stripY == null || this.isOpen()) return;
        if (this.stripY - e.touches[0].clientY > 24) {
          this.stripY = null;
          this.swiped = true;
          setTimeout(() => { this.swiped = false; }, 450);
          this.open(this.toggle);
        }
      }, { passive: true });
      this.toggle.addEventListener('touchend', () => { this.stripY = null; }, { passive: true });
    }

    /* Everything the tray shows is re-rendered by Shopify; read it again after every repaint. */
    if (window.MutationObserver) {
      new MutationObserver(() => this.syncTray()).observe(this, { childList: true, subtree: true });
    }
    document.addEventListener('mad:estimate:changed', () => this.syncTray());
    window.addEventListener('storage', (e) => { if (!e.key || e.key === 'mad_estimate_v1') this.syncTray(); });
    /* Back and forward from another page: the cart may have changed there. */
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
    /* On the full cart page, Craft does not repaint the drawer after a change made on the page. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'cart-items' && document.getElementById('main-cart-items')) this.refresh();
      });
    }

    document.addEventListener('keydown', (e) => this.onKeydown(e));
    this.bindSheetDrag();
    this.watchBottom();
    this.syncTray();
  }

  isOpen() {
    return this.classList.contains('active');
  }

  isPhone() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  /* Repaint the tray from the server (the same section Craft uses). */
  refresh() {
    const items = this.querySelector('cart-drawer-items');
    if (items && typeof items.onCartUpdate === 'function') return items.onCartUpdate();
    return Promise.resolve();
  }

  syncTray() {
    const state = this.querySelector('[data-mad-tray-state]');
    const count = state ? Number(state.getAttribute('data-count')) || 0 : 0;
    const s = window.MadCartTray.summary({
      count,
      total: state ? state.getAttribute('data-total') : 0,
      totalText: state ? state.getAttribute('data-total-text') : '',
      estimate: window.MadCartTray.estimateCount(),
    });
    const setText = (el, text) => { if (el && el.textContent !== text) el.textContent = text; };
    const setHidden = (el, hidden) => { if (el && el.hidden !== hidden) el.hidden = hidden; };

    this.classList.toggle('is-empty', count === 0);

    const row = this.querySelector('[data-mad-tray-estimate]');
    if (row) {
      setHidden(row, !s.estimate);
      setText(row.querySelector('[data-mad-tray-estimate-count]'), s.estimateLine);
    }
    /* Release B: the header icon counts the estimate doors, so an empty cart does not say "Your cart is empty." while
       the estimate has doors (Explore the collection stays) */
    setHidden(this.querySelector('[data-mad-tray-empty-text]'), !!s.estimate);

    const t = this.toggle;
    if (t) {
      setHidden(t.querySelector('[data-mad-tray-cart]'), !s.cart);
      setText(t.querySelector('[data-mad-tray-count]'), s.count);
      setText(t.querySelector('[data-mad-tray-count-word]'), s.countWord);
      setText(t.querySelector('[data-mad-tray-total]'), s.total);
      setHidden(t.querySelector('[data-mad-tray-est]'), !s.estimate);
      setText(t.querySelector('[data-mad-tray-est-count]'), s.estimateCount);
      setText(t.querySelector('[data-mad-tray-est-word]'), s.estimateWord);
      setHidden(t, !(s.visible || this.isOpen()));
    }
    document.documentElement.classList.toggle('mad-tray-has-content', !!(t && s.visible));
    /* A repaint while the tray is open (Remove) must not drop focus onto the page behind it. */
    if (this.isOpen()) {
      const panel = this.querySelector('.drawer__inner');
      const active = document.activeElement;
      if (panel && (!active || active === document.body || !active.isConnected)) panel.focus({ preventScroll: true });
    }
    this.scheduleBottom();
  }

  open(triggeredBy) {
    if (this.isOpen() || document.documentElement.classList.contains('mad-tray-open')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.lockScroll();
    document.documentElement.classList.add('mad-tray-open');
    this.setExpanded(true);
    if (typeof removeTrapFocus === 'function') removeTrapFocus();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      /* The panel is visible from the first frame of the slide, so focus moves now (no wait for the
         transition, which never ends when motion is reduced). */
      requestAnimationFrame(() => {
        const panel = this.querySelector('.drawer__inner');
        if (panel && this.isOpen()) panel.focus({ preventScroll: true });
      });
    });

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.cancelDrag();
    if (!this.isOpen() && !document.documentElement.classList.contains('mad-tray-open')) return;
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    this.unlockScroll();
    document.documentElement.classList.remove('mad-tray-open');
    this.setExpanded(false);
    const status = this.querySelector('[data-mad-tray-status]');
    if (status) status.textContent = '';
    this.syncTray();
    this.returnFocus();
  }

  setExpanded(open) {
    if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    const icon = document.querySelector('#cart-icon-bubble[aria-controls]');
    if (icon) icon.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  returnFocus() {
    const el = this.activeElement;
    const visible = (node) => !!(node && document.contains(node) && !node.closest('[hidden]') && (node.offsetWidth || node.offsetHeight || node.getClientRects().length));
    const target = visible(el) ? el : document.querySelector('#cart-icon-bubble');
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  lockScroll() {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) {
      document.body.style.paddingRight = gap + 'px';
      document.documentElement.style.setProperty('--mad-tray-scrollbar', gap + 'px');
    }
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--mad-tray-scrollbar');
  }

  /* The dialog keeps focus: Tab from the last control goes to the first, Shift+Tab from the first to the last. */
  onKeydown(e) {
    if (!this.isOpen()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;
    const list = window.MadCartTray.focusables(panel);
    if (!list.length) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel || !panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Phone sheet: drag down from its top edge, or from the list when it is scrolled to the top, to close it. */
  bindSheetDrag() {
    this.addEventListener('touchstart', (e) => {
      if (!this.isOpen() || !this.isPhone() || e.touches.length !== 1) return;
      const panel = this.querySelector('.drawer__inner');
      if (!panel || !panel.contains(e.target)) return;
      const scroller = this.querySelector('[data-mad-tray-scroll]');
      const onTop = !!e.target.closest('[data-mad-tray-grab], .mad-tray__header');
      const listAtTop = !!(scroller && scroller.contains(e.target) && scroller.scrollTop <= 0);
      if (!onTop && !listAtTop) return;
      this.drag = { y0: e.touches[0].clientY, t0: Date.now(), dy: 0, panel, moving: false };
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      const d = this.drag;
      if (!d) return;
      const dy = e.touches[0].clientY - d.y0;
      if (!d.moving) {
        if (dy < -6) { this.drag = null; return; } /* scrolling up the list: let it scroll */
        if (dy <= 6) return;
        d.moving = true;
        d.panel.style.transition = 'none';
      }
      e.preventDefault();
      d.dy = Math.max(0, dy);
      d.panel.style.transform = 'translateY(' + d.dy + 'px)';
    }, { passive: false });

    const end = () => {
      const d = this.drag;
      this.drag = null;
      if (!d || !d.moving) return;
      const close = window.MadCartTray.shouldClose(d.dy, Date.now() - d.t0, d.panel.offsetHeight);
      d.panel.style.transition = '';
      d.panel.style.transform = '';
      if (close) this.close();
    };
    this.addEventListener('touchend', end, { passive: true });
    this.addEventListener('touchcancel', end, { passive: true });
  }

  cancelDrag() {
    if (!this.drag) return;
    this.drag.panel.style.transition = '';
    this.drag.panel.style.transform = '';
    this.drag = null;
  }

  /* Phone strip: one bar at the bottom. It steps aside for the door page's sticky purchase bar and while the
     on-screen keyboard is up (the stylesheet also hides it while another dialog or menu is open). */
  watchBottom() {
    const schedule = () => this.scheduleBottom();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (window.MutationObserver) {
      const mo = new MutationObserver(schedule);
      const sticky = document.querySelector('[data-mad-sticky-cta]');
      if (sticky) {
        mo.observe(sticky, { attributes: true });
        sticky.addEventListener('transitionend', schedule);
      }
      mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    const vv = window.visualViewport;
    if (vv) {
      let base = vv.height;
      vv.addEventListener('resize', () => {
        if (vv.height > base) base = vv.height;
        document.documentElement.classList.toggle('mad-tray-keyboard', base - vv.height > 150);
      });
    }
  }

  scheduleBottom() {
    if (this.bottomRaf) return;
    const run = () => {
      this.bottomRaf = 0;
      const sticky = document.querySelector('[data-mad-sticky-cta]');
      document.documentElement.classList.toggle('mad-tray-yield', window.MadCartTray.stickyShowing(sticky));
      if (window.MadChatClear && typeof window.MadChatClear.update === 'function') {
        try { window.MadChatClear.update(); } catch (e) { /* the chat app, not ours */ }
      }
    };
    this.bottomRaf = window.requestAnimationFrame ? window.requestAnimationFrame(run) : setTimeout(run, 16);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* After "Buy door only" (product-form.js): repaint the tray, open it and say which door was added. */
  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
      this.showAdded(parsedState && parsedState.key);
    });
  }

  showAdded(key) {
    if (!key) return;
    const line = Array.prototype.find.call(this.querySelectorAll('[data-line-key]'), (el) => el.getAttribute('data-line-key') === key);
    if (!line) return;
    line.classList.add('is-added');
    const name = line.querySelector('.mad-tray__name');
    const status = this.querySelector('[data-mad-tray-status]');
    if (status) status.textContent = 'Added to your cart' + (name ? ': ' + name.textContent.trim() : '');
    try { line.scrollIntoView({ block: 'nearest' }); } catch (e) { /* old browsers */ }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    /* Craft repaints the drawer after every cart change except its own. Buy door only (product-form) repaints it
       too, through renderContents, with the door it just added; a second repaint here would wipe that. */
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form') return;
      return this.onCartUpdate();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /* A change made elsewhere (the estimate form, the full cart page): repaint the whole tray,
     so the lines, the costs, "Before you order" and Check out always agree. */
  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('#CartDrawer .drawer__inner');
        const target = document.querySelector('#CartDrawer .drawer__inner');
        if (source && target) target.innerHTML = source.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
